import React, { createContext, useContext, useState, useEffect } from 'react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const FeatureFlagsContext = createContext();

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
};

export const FeatureFlagsProvider = ({ children }) => {
  const [featureFlags, setFeatureFlags] = useState({});
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);

  // Move fetchFeatureFlags outside useEffect to prevent recreation
  const fetchFeatureFlags = async () => {
    // Prevent multiple simultaneous fetches
    if (isFetching) {
      console.log('🔄 Already fetching feature flags, skipping...');
      return;
    }
    
    try {
      setIsFetching(true);
      setLoading(true);
      console.log('🔍 Fetching feature flags from:', API_BASE_URL);
      const response = await fetch(`${API_BASE_URL}/api/feature-flags`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Feature flags loaded successfully');
        setFeatureFlags(data.feature_flags || {});
      } else {
        console.warn('Failed to fetch feature flags, using defaults. Status:', response.status);
        setFeatureFlags({
          master_flag: false,
          college_planning: false,
          subscription_plans: false,
          notification_bell: false,
          merchandising: false,
          elearn: false,
          learn_pro: false,
          ai_guidance: false,
          ai_coach: false,
          ai_forecast: false,
          pro_invest: false,
          ai_coach_forecast_invest: false,
          premium_gaming: false,
          round_ups: false,
          round_ups_investment: false,
          investment_fund: false,
          total_savings_transfer: false,
          check_in: false
        });
      }
    } catch (error) {
      console.error('❌ Error fetching feature flags:', error);
      setFeatureFlags({
        master_flag: false,
        college_planning: false,
        subscription_plans: false,
        notification_bell: false,
        merchandising: false,
        elearn: false,
        learn_pro: false,
        ai_guidance: false,
        ai_coach: false,
        ai_forecast: false,
        pro_invest: false,
        ai_coach_forecast_invest: false,
        premium_gaming: false,
        round_ups: false,
        round_ups_investment: false,
        investment_fund: false,
        total_savings_transfer: false,
        check_in: false
      });
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  useEffect(() => {
    // Only fetch once on mount
    fetchFeatureFlags();
    
    // Track user activity
    let lastActivity = Date.now();
    let interval;
    
    // Update last activity on user interactions
    const updateActivity = () => {
      lastActivity = Date.now();
    };
    
    // Add event listeners for user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });
    
    // Set up periodic refresh only when user is active (within last 30 seconds)
    interval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivity;
      
      // Only fetch if user was active within last 30 seconds (30000ms)
      if (timeSinceLastActivity <= 30000) {
        console.log('🔄 Periodic feature flags refresh (user active)');
        fetchFeatureFlags();
      } else {
        console.log('⏸️ Skipping feature flags refresh (user inactive for', Math.round(timeSinceLastActivity / 1000), 'seconds)');
      }
    }, 60000);
    
    return () => {
      clearInterval(interval);
      // Remove event listeners
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
    };
  }, []); // Empty dependency array to prevent loops

  const isFeatureEnabled = (flagName) => {
    // If master flag is off, no features are enabled
    if (!featureFlags.master_flag) {
      return false;
    }
    
    // If master flag is on, check individual flag state
    return featureFlags[flagName] === true;
  };

  const refreshFeatureFlags = () => {
    console.log('🔄 Manual feature flags refresh');
    fetchFeatureFlags();
  };

  return (
    <FeatureFlagsContext.Provider 
      value={{ 
        featureFlags, 
        isFeatureEnabled, 
        loading,
        refreshFeatureFlags
      }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  );
};